import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { InspectionInfo } from '../../lib/db/auth';

/**
 * Aviso fijo mientras se inspecciona la cuenta de otro usuario.
 *
 * Es deliberadamente imposible de ignorar: ocupa el ancho completo, empuja el contenido hacia
 * abajo en vez de flotar sobre él, y no se puede cerrar. El riesgo que cubre no es estético —un
 * administrador que olvida que está dentro de la cuenta de un cliente acaba creyendo que los
 * datos que ve son suyos, y toma decisiones con la contabilidad equivocada delante.
 */
export default function InspectionBanner() {
  const [info, setInfo] = useState<InspectionInfo | null>(null);
  const [saliendo, setSaliendo] = useState(false);

  useEffect(() => {
    const leer = () => setInfo(supabase.auth.inspectionInfo());
    leer();

    // La inspección puede empezar o terminar en otra pestaña.
    const onStorage = () => leer();
    window.addEventListener('storage', onStorage);
    const { data } = supabase.auth.onAuthStateChange(() => leer());

    return () => {
      window.removeEventListener('storage', onStorage);
      data.subscription.unsubscribe();
    };
  }, []);

  if (!info) return null;

  const salir = async () => {
    setSaliendo(true);
    await supabase.auth.stopInspection();
    // Recarga completa por lo mismo que al entrar: las pantallas cachean datos en memoria.
    window.location.href = '/admin/inspeccionar';
  };

  return (
    <div className="sticky top-0 z-[1100] bg-amber-500 text-amber-950 shadow-md print:hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-lg leading-none" aria-hidden>
            👁️
          </span>
          <p className="text-sm font-medium truncate">
            Estás inspeccionando la cuenta de{' '}
            <strong className="font-bold">{info.targetEmail}</strong>
            <span className="hidden sm:inline">
              {' '}
              · Modo simulación: nada de lo que hagas afecta a sus datos reales
            </span>
          </p>
        </div>
        <button
          onClick={salir}
          disabled={saliendo}
          className="shrink-0 px-4 py-1.5 bg-amber-950 text-amber-50 text-sm font-semibold rounded-lg hover:bg-black disabled:opacity-60"
        >
          {saliendo ? 'Saliendo…' : 'Salir de la inspección'}
        </button>
      </div>
    </div>
  );
}
